import React, { useState, useEffect } from "react";
import { Box, Typography, Avatar, Button, Chip, List, ListItem, Snackbar, Alert, CircularProgress } from "@mui/material";
import { Edit, LocationOn, Info as InfoIcon, AccessTime } from "@mui/icons-material";
import ChangePassword from "../../components/Profile/ChangePassword";
import EditProfile from "../../components/Profile/EditProfile";
import { getUserProfile } from "../../services/UserProfile";
import { IUserProfile } from "../../interfaces/UserProfile";

interface ActivityLog {
  id: string;
  title: string;
  description: string;
  date: string;
  time: string;
}

const activityLogs: ActivityLog[] = [
  { id: "1", title: "Moved item 849839383", description: "Item 849383939 moved from Transit room to building A", date: "Today", time: "10:42 AM" },
  { id: "2", title: "Received item 839499494", description: "Item 839488494 is Received and send to floor 2/ Room 2", date: "Today", time: "09:15 AM" },
  { id: "3", title: "Received item 839499494", description: "Item 839488494 is Received and Transferred to Rack 2/ bin 2", date: "Yesterday", time: "04:30 PM" },
  { id: "4", title: "Added Order", description: "Order 74848484 is Taken from Ravi kapoor", date: "Yesterday", time: "04:30 PM" },
  { id: "5", title: "Added Order", description: "Order 74848484 is Taken from Ravi kapoor", date: "Oct 24", time: "11:00 AM" },
  { id: "6", title: "Password Changed", description: "User self-initiated reset", date: "Oct 24", time: "11:00 AM" },
  { id: "7", title: "Moved item 839383939", description: "Item 839383939 moved from building A to shipping dock", date: "Today", time: "11:22 AM" },
  { id: "8", title: "Shipped item 839499494", description: "Item 839499494 has been shipped to warehouse 5", date: "Today", time: "12:00 PM" },
];

const MyProfile = () => {
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [userProfile, setUserProfile] = useState<IUserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");

  useEffect(() => {
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    try {
      setLoading(true);
      const profile = await getUserProfile();
      setUserProfile(profile);
    } catch (error) {
      console.error("Error loading profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccess = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarSeverity("success");
    setShowSnackbar(true);
    loadUserProfile(); // Reload profile data
  };

  if (showEditProfile) {
    return <EditProfile onCancel={() => setShowEditProfile(false)} onSave={(message) => { setShowEditProfile(false); handleSuccess(message); }} />;
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ height: "100%", overflow: "auto", p: 1 }}>
      <Box sx={{ bgcolor: "white", borderRadius: "0.75rem", p: 1, border: "1px solid", borderColor: "grey.100", height: "100%", display: "flex", flexDirection: "column" }}>
        <Box sx={{ bgcolor: "background.default", borderRadius: "0.75rem", p: 0.5, mb: 1, display: "flex", alignItems: "center", gap: 2, border: "1px solid rgba(74,85,101,0.1)" }}>
          <Box sx={{ position: "relative", flexShrink: 0 }}>
            <Avatar sx={{ width: 80, height: 80, border: "1px solid", borderColor: "grey.100", boxShadow: "0 2px 4px 0 rgba(0,0,0,0)" }}>
              {userProfile?.profileImgUrl ? (
                <img 
                  src={userProfile.profileImgUrl} 
                  alt="Profile" 
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} 
                />
              ) : (
                <img 
                  src="/Prowess_logo.png" 
                  alt="Default Profile" 
                  style={{ width: '60%', height: '60%', objectFit: 'contain' }} 
                />
              )}
            </Avatar>
            <Box sx={{ position: "absolute", bottom: 4, right: 4, width: 20, height: 20, borderRadius: "50%", bgcolor: "success.main", border: "2px solid white" }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: "text.primary", fontSize: "1.5rem", fontWeight: 700 }}>{userProfile?.firstName || '-'} {userProfile?.lastName || ''}</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ color: "grey.700", fontSize: "0.875rem", fontWeight: 500 }}>{userProfile?.roleName || '-'}</Typography>
              <Chip 
                label={userProfile?.status || "Active"}
                size="small" 
                icon={<Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: userProfile?.status === "Active" ? "success.main" : "error.main", ml: 1 }} />}
                sx={{ 
                  bgcolor: userProfile?.status === "Active" ? "rgba(52, 199, 89, 0.10)" : "rgba(255, 59, 48, 0.10)",
                  color: userProfile?.status === "Active" ? "success.main" : "error.main",
                  fontWeight: 700, 
                  fontSize: "0.75rem", 
                  height: "auto", 
                  borderRadius: "9999px", 
                  py: 0.3, 
                  px: 1, 
                  "& .MuiChip-label": { px: 0.5 }, 
                  "& .MuiChip-icon": { ml: 0, mr: 0.5 } 
                }} 
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <LocationOn sx={{ color: "grey.700", fontSize: "1rem", width: 16, height: 24 }} />
              <Typography sx={{ color: "grey.700", fontSize: "0.875rem", fontWeight: 400 }}>{userProfile?.workspaceName || '-'}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
            <Button 
              variant="outlined" 
              onClick={() => setShowChangePassword(true)}
              sx={{ borderRadius: "0.5rem", border: "1px solid", borderColor: "grey.100", color: "text.primary", fontSize: "0.875rem", fontWeight: 500, textTransform: "none", "&:hover": { bgcolor: "transparent", border: "1px solid", borderColor: "grey.100" } }}
            >
              Change Password
            </Button>
            <Button 
              variant="contained" 
              startIcon={<Edit sx={{ fontSize: "1.125rem !important", width: 18, height: 28 }} />} 
              onClick={() => setShowEditProfile(true)}
              sx={{ borderRadius: "0.5rem", bgcolor: "primary.main", boxShadow: "0 2px 4px 0 rgba(0,0,0,0)", color: "white", fontSize: "0.875rem", fontWeight: 500, textTransform: "none", "&:hover": { bgcolor: "primary.main" } }}
            >
              Edit Profile
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, mt: 2 }}>
              <InfoIcon sx={{ color: "primary.main" }} />
              <Typography sx={{ color: "text.primary", fontSize: "1rem", fontWeight: 700 }}>General Info</Typography>
            </Box>
            <Box sx={{ bgcolor: "background.default", borderRadius: "0.75rem", p: 2, display: "flex", flexDirection: "column", gap: 2, border: "1px solid", borderColor: "grey.100", overflow: "auto", "&::-webkit-scrollbar": { width: 6 }, "&::-webkit-scrollbar-thumb": { background: "#cbd5e1", borderRadius: "4px" } }}>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
                {[
                  { label: "Employee ID", value: userProfile?.empId },
                  { label: "Department", value: userProfile?.departmentName },
                  { label: "Phone Number", value: userProfile?.phone },
                ].map((item) => (
                  <Box key={item.label}>
                    <Typography sx={{ color: "grey.700", fontSize: "0.75rem", fontWeight: 500, letterSpacing: "0.6px", textTransform: "uppercase" }}>{item.label}</Typography>
                    <Typography sx={{ color: "text.primary", fontSize: "0.875rem", fontWeight: 600, wordBreak: "break-all" }}>{item.value || '-'}</Typography>
                  </Box>
                ))}
                <Box>
                  <Typography sx={{ color: "grey.700", fontSize: "0.75rem", fontWeight: 500, letterSpacing: "0.6px", textTransform: "uppercase" }}>Email Address</Typography>
                  <Typography sx={{ color: "text.primary", fontSize: "0.875rem", fontWeight: 600, wordBreak: "break-all" }}>{userProfile?.email || '-'}</Typography>
                </Box>
                <Box sx={{ gridColumn: "span 2" }}>
                  <Typography sx={{ color: "grey.700", fontSize: "0.75rem", fontWeight: 500, letterSpacing: "0.6px", textTransform: "uppercase", mb: 1 }}>Invited by</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar sx={{ width: 40, height: 40, bgcolor: "background.default", border: "1px solid", borderColor: "grey.100" }}>
                      {userProfile?.createdByProfileImgUrl ? (
                        <img src={userProfile.createdByProfileImgUrl} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                      ) : (
                        <img src="/sidebar_logo.png" alt="Default Profile" style={{ width: '60%', height: '60%', objectFit: 'contain' }} />
                      )}
                    </Avatar>
                    <Box>
                      <Typography sx={{ color: "text.primary", fontSize: "0.875rem", fontWeight: 700 }}>{userProfile?.createdByName || '-'}</Typography>
                      <Typography sx={{ color: "grey.700", fontSize: "0.75rem", fontWeight: 400 }}>{userProfile?.createdBy || '-'}</Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <AccessTime sx={{ color: "primary.main" }} />
              <Typography sx={{ color: "text.primary", fontSize: "1rem", fontWeight: 700 }}>Recent Activity logs</Typography>
            </Box>
            <Box sx={{ bgcolor: "background.default", borderRadius: "0.75rem", p: 2, flex: 1, border: "1px solid", borderColor: "grey.100", overflow: "hidden" }}>
              <List sx={{ bgcolor: "white", borderRadius: "0.5rem", height: "100%", overflow: "auto", p: 0, "&::-webkit-scrollbar": { width: 8 }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "#cbd5e1", borderRadius: "4px" } }}>
                {activityLogs.map((log, i) => (
                  <ListItem key={log.id} divider={i < activityLogs.length - 1} sx={{ py: 1, px: 2, alignItems: "flex-start" }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ color: "text.primary", fontSize: "0.875rem", fontWeight: 500 }}>{log.title}</Typography>
                      <Typography sx={{ color: "grey.700", fontSize: "0.75rem", fontWeight: 400 }} noWrap>{log.description}</Typography>
                    </Box>
                    <Box sx={{ textAlign: "right", ml: 2, flexShrink: 0 }}>
                      <Typography sx={{ color: "grey.700", fontSize: "0.75rem", fontWeight: 400 }}>{log.date}</Typography>
                      <Typography sx={{ color: "grey.700", fontSize: "0.75rem", fontWeight: 500 }}>{log.time}</Typography>
                    </Box>
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box> */}
        </Box>
      </Box>
      </Box>
      <ChangePassword open={showChangePassword} onClose={() => setShowChangePassword(false)} />
      
      <Snackbar 
        open={showSnackbar} 
        autoHideDuration={3000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setShowSnackbar(false)} 
          severity={snackbarSeverity}
          variant="filled"
          sx={{ 
            width: "100%", 
            backgroundColor: `${snackbarSeverity === "success" ? "success.main" : "error.main"} !important`,
            color: "white",
            "& .MuiAlert-icon": { color: "white" },
            "& .MuiAlert-action": { color: "white" }
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MyProfile;
